import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentFormat, DocumentLanguage } from '../../../api/documents';
import { isApiError } from '../../../api/errors';
import { Button, ButtonGroup, Select } from '../../../components/idsk';
import { currentLanguage, LANGUAGE_AUTONYMS } from '../../../i18n';
import { useAnnouncer } from '../../../layout/Announcer';

export function defaultDocumentLanguage(): DocumentLanguage {
  return currentLanguage() === 'en' ? 'en' : 'sk';
}

/** "Jazyk dokumentu": Slovak or English (option names in their own language). */
export function DocumentLanguageSelect({ id, value, onChange, hint }: { id: string; value: DocumentLanguage; onChange: (lang: DocumentLanguage) => void; hint?: string }) {
  const { t } = useTranslation();
  return (
    <Select
      id={id}
      label={t('documents.language')}
      hint={hint ?? t('documents.languageHint')}
      options={[
        { value: 'sk', label: LANGUAGE_AUTONYMS.sk, lang: 'sk' },
        { value: 'en', label: LANGUAGE_AUTONYMS.en, lang: 'en' },
      ]}
      value={value}
      onChange={(e) => onChange(e.target.value === 'en' ? 'en' : 'sk')}
    />
  );
}

/**
 * PDF and DOCX buttons for one document. The accessible names say which document is downloaded; progress and the
 * result are announced in the polite live region, failures are shown next to the buttons.
 */
export function DownloadButtons({
  id,
  name,
  lang,
  onDownload,
}: {
  id: string;
  /** What is downloaded, e.g. the form title (visually hidden part of the button names). */
  name: string;
  lang: DocumentLanguage;
  onDownload: (format: DocumentFormat, lang: DocumentLanguage) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { announce, alert } = useAnnouncer();
  const busy = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (format: DocumentFormat) => {
    if (busy.current) return;
    busy.current = true;
    setError(null);
    const label = format.toUpperCase();
    announce(t('documents.downloading', { name, format: label }));
    try {
      await onDownload(format, lang);
      announce(t('documents.downloaded', { name, format: label }));
    } catch (e) {
      const text = isApiError(e) ? e.message : t('documents.failed');
      setError(text);
      alert(text);
    } finally {
      busy.current = false;
    }
  };

  return (
    <div className="app-downloads">
      <ButtonGroup>
        <Button id={`${id}-pdf`} variant="secondary" onClick={() => void run('pdf')}>
          {t('documents.downloadPdf')} <span className="govuk-visually-hidden">{t('documents.hiddenName', { name })}</span>
        </Button>
        <Button id={`${id}-docx`} variant="secondary" onClick={() => void run('docx')}>
          {t('documents.downloadDocx')} <span className="govuk-visually-hidden">{t('documents.hiddenName', { name })}</span>
        </Button>
      </ButtonGroup>
      {error && (
        <p className="govuk-error-message" id={`${id}-error`}>
          <span className="govuk-visually-hidden">{t('common.errorPrefix')}</span> {error}
        </p>
      )}
    </div>
  );
}
